import { useState, useEffect, useRef, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "https://api.semiotix.ai";

// ── Color helpers ─────────────────────────────────────────────────────────

const TYPE_COLORS = {
  entity:  { fill: "#3d6fb5", muted: "#1e3a5f" },
  phrase:  { fill: "#4a9b6f", muted: "#1e4a30" },
};

function miColor(mi, max) {
  if (!mi || max === 0) return "transparent";
  const t = mi / max;
  const r = Math.round(20  + t * (240 - 20));
  const g = Math.round(30  + t * (180 - 30));
  const b = Math.round(60  + t * (60  - 60));
  return `rgb(${r},${g},${b})`;
}

// ── Screen A component ────────────────────────────────────────────────────

export default function ScreenA({ query, onSelection }) {
  const [pairs,       setPairs]       = useState([]);
  const [nodes,       setNodes]       = useState([]);
  const [mode,        setMode]        = useState("mix");
  const [sortBy,      setSortBy]      = useState("degree");
  const [selected,    setSelected]    = useState(new Set());
  const [hovered,     setHovered]     = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [status,      setStatus]      = useState("");
  const [perspective, setPerspective] = useState("intelligence");
  const [insight,     setInsight]     = useState(null);
  const [analyzing,   setAnalyzing]   = useState(false);
  const svgRef = useRef(null);

  // ── Fetch MI pairs ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    setStatus("Loading…");
    setInsight(null);
    fetch(`${API}/query/${encodeURIComponent(query.toLowerCase())}/mi`)
      .then(r => r.json())
      .then(data => {
        setPairs(data.pairs || []);
        setStatus(`${data.n_docs} docs · ${(data.pairs||[]).length} pairs`);
      })
      .catch(e => setStatus("Error: " + e.message))
      .finally(() => setLoading(false));
  }, [query]);

  // ── Compute nodes ───────────────────────────────────────────────────────

  useEffect(() => {
    const nodeMap = {};
    pairs.forEach(p => {
      const [n1, n2] = p.pair;
      const [t1, t2] = p.type;
      const [l1, l2] = p.label || [null, null];
      if (mode === "entities" && (t1 !== "entity" || t2 !== "entity")) return;
      if (mode === "phrases"  && (t1 !== "phrase"  || t2 !== "phrase"))  return;
      if (!nodeMap[n1]) nodeMap[n1] = { name: n1, type: t1, label: l1, degree: 0 };
      if (!nodeMap[n2]) nodeMap[n2] = { name: n2, type: t2, label: l2, degree: 0 };
      nodeMap[n1].degree += p.ncooc;
      nodeMap[n2].degree += p.ncooc;
    });
    let list = Object.values(nodeMap);
    if (mode === "entities") list = list.filter(n => n.type === "entity");
    if (mode === "phrases")  list = list.filter(n => n.type === "phrase");
    if (sortBy === "degree") list.sort((a,b) => b.degree - a.degree);
    else list.sort((a,b) => a.name.localeCompare(b.name));
    setNodes(list.slice(0, 40));
  }, [pairs, mode, sortBy]);

  // ── Pair lookup ─────────────────────────────────────────────────────────

  const pairLookup = useCallback(() => {
    const lu = {};
    pairs.forEach(p => {
      lu[`${p.pair[0]}|${p.pair[1]}`] = p;
      lu[`${p.pair[1]}|${p.pair[0]}`] = p;
    });
    return lu;
  }, [pairs]);

  // ── Agent call ──────────────────────────────────────────────────────────

  async function analyze(selectionList) {
    if (!pairs.length) return;
    // read current selection directly from state to avoid stale closure
    const currentSelection = selectionList.length ? selectionList : null;
    setAnalyzing(true);
    setInsight(null);
    try {
      const r = await fetch(`${API}/agent/a`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query,
          pairs:       pairs.slice(0, 50),
          selection:   currentSelection,
          perspective,
        }),
      });
      const data = await r.json();
      setInsight(data);
    } catch(e) {
      setInsight({ insight: `Error: ${e.message}`, suggestions: [], evidence: [] });
    } finally {
      setAnalyzing(false);
    }
  }

  // ── Node click ──────────────────────────────────────────────────────────

  function handleNodeClick(name) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      if (onSelection) onSelection([...next]);
      return next;
    });
  }

  // ── Render ──────────────────────────────────────────────────────────────

  const lookup = pairLookup();
  const maxMI  = Math.max(...pairs.map(p => p.mi_index), 0.001);
  const CELL   = 18;
  const LABEL  = 120;
  const N      = nodes.length;
  const W      = LABEL + N * CELL + 8;
  const H      = LABEL + N * CELL + 8;
  const selArr = [...selected];

  return (
    <div style={styles.wrap}>

      {/* Header */}
      <div style={styles.header}>
        <span style={styles.title}>A — Matrix</span>
        <span style={styles.subtitle}>co-occurrence</span>

        <div style={styles.controls}>
          <span style={styles.ctrlLabel}>nodes</span>
          {["mix","entities","phrases"].map(m => (
            <button key={m}
              style={{...styles.btn, ...(mode===m ? styles.btnActive : {})}}
              onClick={() => setMode(m)}>{m}</button>
          ))}
        </div>

        <div style={styles.controls}>
          <span style={styles.ctrlLabel}>sort</span>
          {["degree","alpha"].map(s => (
            <button key={s}
              style={{...styles.btn, ...(sortBy===s ? styles.btnActive : {})}}
              onClick={() => setSortBy(s)}>{s}</button>
          ))}
        </div>

        <div style={styles.controls}>
          <span style={styles.ctrlLabel}>perspective</span>
          {["intelligence","legal","criticism","education"].map(p => (
            <button key={p}
              style={{...styles.btn, ...(perspective===p ? styles.btnActive : {})}}
              onClick={() => setPerspective(p)}>{p}</button>
          ))}
        </div>

        <button
          style={{...styles.analyzeBtn, ...(analyzing ? styles.analyzeBtnBusy : {})}}
          onClick={() => analyze([...selected])}
          disabled={analyzing || !pairs.length}
        >
          {analyzing ? "thinking…" : selArr.length ? `analyze cluster` : "analyze map"}
        </button>

        <span style={styles.status}>{loading ? "loading…" : status}</span>
      </div>

      {/* Matrix */}
      {N > 0 && (
        <div style={styles.matrixWrap}>
          <svg ref={svgRef} width={W} height={H} style={{overflow:"visible"}}>

            {/* Column labels */}
            {nodes.map((n, j) => (
              <g key={`col-${j}`}
                 transform={`translate(${LABEL + j*CELL + CELL/2}, ${LABEL - 4})`}>
                <text transform="rotate(-45)" textAnchor="start" fontSize={9}
                  fontFamily="DM Mono, monospace" style={{pointerEvents:"none"}}
                  fill={selected.has(n.name) ? "#f0c060"
                        : TYPE_COLORS[n.type]?.fill || "#6b6878"}>
                  {n.name.length > 16 ? n.name.slice(0,15)+"…" : n.name}
                </text>
              </g>
            ))}

            {/* Rows + cells */}
            {nodes.map((row, i) => (
              <g key={`row-${i}`}>
                <text x={LABEL-6} y={LABEL+i*CELL+CELL*0.65}
                  textAnchor="end" fontSize={9} fontFamily="DM Mono, monospace"
                  style={{pointerEvents:"none"}}
                  fill={selected.has(row.name) ? "#f0c060"
                        : TYPE_COLORS[row.type]?.fill || "#6b6878"}>
                  {row.name.length > 18 ? row.name.slice(0,17)+"…" : row.name}
                </text>

                {nodes.map((col, j) => {
                  if (i === j) return (
                    <rect key={`d-${i}`}
                      x={LABEL+j*CELL} y={LABEL+i*CELL}
                      width={CELL-1} height={CELL-1}
                      fill="#1e1e28" rx={1}/>
                  );
                  const pair = lookup[`${row.name}|${col.name}`];
                  const mi   = pair?.mi_index || 0;
                  const isHL = selected.size === 0 ||
                               selected.has(row.name) || selected.has(col.name);
                  return (
                    <rect key={`c-${i}-${j}`}
                      x={LABEL+j*CELL} y={LABEL+i*CELL}
                      width={CELL-1} height={CELL-1}
                      fill={mi > 0 ? miColor(mi, maxMI) : "#141418"}
                      fillOpacity={isHL ? 1 : 0.2}
                      rx={1}
                      style={{cursor: mi > 0 ? "pointer" : "default"}}
                      onClick={() => { if(mi > 0) { handleNodeClick(row.name); handleNodeClick(col.name); }}}
                      onMouseEnter={() => setHovered(`${row.name}|${col.name}|${mi.toFixed(2)}`)}
                      onMouseLeave={() => setHovered(null)}
                    />
                  );
                })}
              </g>
            ))}

            {/* Selection lines */}
            {nodes.map((n, i) => selected.has(n.name) && (
              <g key={`sel-${i}`}>
                <line x1={LABEL+i*CELL} y1={LABEL} x2={LABEL+i*CELL} y2={LABEL+N*CELL}
                  stroke="#f0c060" strokeWidth={0.5} strokeOpacity={0.3}/>
                <line x1={LABEL} y1={LABEL+i*CELL} x2={LABEL+N*CELL} y2={LABEL+i*CELL}
                  stroke="#f0c060" strokeWidth={0.5} strokeOpacity={0.3}/>
              </g>
            ))}
          </svg>
        </div>
      )}

      {/* Tooltip */}
      {hovered && hovered.includes("|") && (
        <div style={styles.tooltip}>
          {hovered.split("|")[0]} ↔ {hovered.split("|")[1]}
          <span style={{color:"#f0c060", marginLeft:8}}>mi: {hovered.split("|")[2]}</span>
        </div>
      )}

      {/* Selection bar */}
      <div style={styles.selBar}>
        <span style={styles.ctrlLabel}>selection</span>
        {selected.size === 0
          ? <span style={{color:"#3a3a45",fontSize:10}}>click a node to select</span>
          : selArr.map(n => <span key={n} style={styles.tag}>{n}</span>)
        }
        {selected.size > 0 && (
          <button style={styles.clearBtn}
            onClick={() => { setSelected(new Set()); if(onSelection) onSelection([]); }}>
            clear
          </button>
        )}
      </div>

      {/* Insight panel */}
      {(analyzing || insight) && (
        <div style={styles.insightPanel}>
          {analyzing && (
            <div style={styles.insightLoading}>
              <span style={{color:"#6b6878",fontSize:10,letterSpacing:"0.2em"}}>
                AGENT A · {perspective.toUpperCase()} · thinking…
              </span>
            </div>
          )}
          {insight && !analyzing && (
            <>
              <div style={styles.insightHeader}>
                AGENT A · {(insight.perspective||perspective).toUpperCase()}
                {insight.selection?.length ? ` · ${insight.selection.join(", ")}` : " · MAP"}
              </div>
              <div style={styles.insightText}>{insight.insight}</div>
              {insight.suggestions?.length > 0 && (
                <div style={styles.suggestions}>
                  {insight.suggestions.map((s,i) => (
                    <div key={i} style={styles.suggestion}>→ {s}</div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const styles = {
  wrap: {
    display:"flex", flexDirection:"column", height:"100%",
    background:"#0d0d0f", color:"#e8e4dc",
    fontFamily:"'DM Mono', monospace", fontSize:12, overflow:"hidden",
  },
  header: {
    display:"flex", alignItems:"center", gap:8, padding:"10px 16px",
    borderBottom:"1px solid #2a2a35", flexShrink:0, flexWrap:"wrap",
  },
  title: { fontFamily:"Georgia, serif", fontSize:17, fontWeight:300 },
  subtitle: { color:"#6b6878", fontSize:9, letterSpacing:"0.2em", textTransform:"uppercase" },
  controls: {
    display:"flex", alignItems:"center", gap:3, marginLeft:8,
    paddingLeft:8, borderLeft:"1px solid #2a2a35",
  },
  ctrlLabel: {
    color:"#6b6878", fontSize:9, letterSpacing:"0.2em",
    textTransform:"uppercase", marginRight:3,
  },
  btn: {
    background:"transparent", border:"1px solid transparent",
    color:"#6b6878", fontFamily:"'DM Mono', monospace", fontSize:9,
    padding:"2px 6px", cursor:"pointer", borderRadius:2,
  },
  btnActive: { background:"#2a2a35", color:"#f0c060", borderColor:"#f0c060" },
  analyzeBtn: {
    marginLeft:"auto", background:"#f0c060", border:"none", borderRadius:2,
    color:"#0d0d0f", fontFamily:"'DM Mono', monospace", fontSize:10,
    fontWeight:600, padding:"4px 12px", cursor:"pointer", letterSpacing:"0.1em",
    textTransform:"uppercase",
  },
  analyzeBtnBusy: { background:"#3a3a45", color:"#6b6878", cursor:"default" },
  status: { color:"#3a3a45", fontSize:10, marginLeft:4 },
  matrixWrap: { flex:1, overflow:"auto", padding:"16px 16px 0" },
  tooltip: {
    position:"fixed", bottom:100, left:"50%", transform:"translateX(-50%)",
    background:"#141418", border:"1px solid #2a2a35", padding:"5px 10px",
    borderRadius:3, fontSize:11, pointerEvents:"none", zIndex:100,
  },
  selBar: {
    display:"flex", alignItems:"center", gap:6, padding:"5px 16px",
    borderTop:"1px solid #2a2a35", flexShrink:0, minHeight:28,
  },
  tag: {
    background:"#2a2a35", color:"#f0c060",
    padding:"1px 6px", borderRadius:2, fontSize:10,
  },
  clearBtn: {
    marginLeft:"auto", background:"transparent", border:"1px solid #2a2a35",
    color:"#6b6878", fontFamily:"'DM Mono', monospace", fontSize:10,
    padding:"2px 7px", cursor:"pointer", borderRadius:2,
  },
  insightPanel: {
    borderTop:"1px solid #2a2a35", padding:"12px 16px",
    flexShrink:0, maxHeight:200, overflowY:"auto",
    background:"#0a0a0c",
  },
  insightLoading: {
    display:"flex", alignItems:"center", justifyContent:"center", height:40,
  },
  insightHeader: {
    color:"#f0c060", fontSize:9, letterSpacing:"0.2em",
    textTransform:"uppercase", marginBottom:8,
  },
  insightText: {
    color:"#e8e4dc", fontSize:12, lineHeight:1.7, marginBottom:10,
  },
  suggestions: { display:"flex", flexDirection:"column", gap:5 },
  suggestion: { color:"#9a96a0", fontSize:11, lineHeight:1.5 },
};
